import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Eye, Globe2, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { buildAssetUrl } from "../../data/authStorage";
import { createInitialsAvatar } from "../../utils/initialsAvatar";

const COMPANY = {
  name: "CEPHAS ICT HUB",
  tagline: "Training, Building and Promoting for Community Development and Global Excellence",
  phone: "+234 8063260342; +234 7032490411",
  email: "johnson.ibironke@cephasict.com; admin@cephasict.com",
  website: "www.cephasict.com",
  linkedin: "cephas ict hub",
  address: "Cephas ICT HUB, LAUTECH Area, Ogbomoso, Oyo State, Nigeria.",
};

const getEmployeeName = (employee) =>
  employee?.fullName ||
  employee?.name ||
  `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim() ||
  "Unnamed Staff";

const getPosition = (employee) =>
  employee?.position?.name ||
  employee?.positionName ||
  employee?.jobTitle ||
  employee?.department?.name ||
  employee?.departmentName ||
  "Staff";

const getEmployeePhoto = (employee, photo) =>
  photo || buildAssetUrl(employee?.profilePhotoUrl) || employee?.profilePhotoUrl || employee?.image || createInitialsAvatar(getEmployeeName(employee));

const getDownloadName = (employee, side) => {
  const name = getEmployeeName(employee).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return `${name || "staff"}-id-card-${side}.png`;
};

function ContactRow({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#ff8a17] text-white">
        <Icon className="h-4 w-4" strokeWidth={2.5} />
      </span>
      <span className="min-w-0 flex-1 break-words text-[13px] leading-tight text-white">{children}</span>
    </div>
  );
}

function CephasMark() {
  return (
    <div className="flex items-center gap-4">
      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full bg-[#ff8a17]">
        <div className="absolute inset-x-1 top-2 h-2 rounded-full bg-white" />
        <div className="absolute inset-x-0 top-5 h-2 rounded-full bg-white" />
        <div className="absolute inset-x-0 top-8 h-2 rounded-full bg-white" />
        <div className="absolute inset-x-2 top-11 h-2 rounded-full bg-white" />
      </div>
      <div>
        <p className="text-[24px] font-extrabold leading-none text-[#101827]">CEPHAS ICT HUB</p>
        <p className="mt-1 max-w-[310px] text-[12px] font-semibold italic leading-tight text-[#1b2538]">
          {COMPANY.tagline}
        </p>
      </div>
    </div>
  );
}

function CardShell({ children }) {
  return (
    <div className="relative h-[480px] w-[360px] overflow-hidden bg-white font-Outfit text-white shadow-lg">
      <div className="absolute inset-0 opacity-45 [background-image:radial-gradient(#9ca3af_1.4px,transparent_1.4px)] [background-size:10px_10px]" />
      <div className="absolute left-0 right-0 top-[150px] h-[120px] bg-[#07172e] [clip-path:ellipse(78%_42%_at_80%_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-[286px] bg-[#07172e]" />
      <div className="absolute inset-x-0 bottom-0 h-[180px] opacity-50 [background-image:radial-gradient(#1d64c8_1.3px,transparent_1.3px)] [background-size:8px_8px]" />
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
}

function FrontCard({ employee, photo }) {
  const fullName = getEmployeeName(employee);
  const [firstName, ...restName] = fullName.split(" ");
  const displayEmail = employee?.officialEmail || employee?.personalEmail || employee?.emailAddress || "-";
  const displayPhone = employee?.phone || employee?.phoneNumber || COMPANY.phone;

  return (
    <CardShell>
      <div className="flex h-full flex-col px-8 pb-9 pt-10">
        <div className="mx-auto h-[210px] w-[210px] overflow-hidden rounded-2xl bg-[#dedbeb]">
          <img src={getEmployeePhoto(employee, photo)} alt={fullName} className="h-full w-full object-cover" crossOrigin="anonymous" />
        </div>

        <div className="mt-7 text-center">
          <p className="truncate text-[24px] leading-tight">
            <span className="font-extrabold">{firstName}</span>
            {restName.length ? ` ${restName.join(" ")}` : ""}
          </p>
          <p className="truncate text-[19px] italic leading-tight text-[#ff8a17]">{getPosition(employee)}</p>
        </div>

        <div className="mt-8 space-y-5">
          <ContactRow icon={Phone}>{displayPhone}</ContactRow>
          <ContactRow icon={Mail}>{displayEmail}</ContactRow>
        </div>
      </div>
    </CardShell>
  );
}

function BackCard({ employee }) {
  const displayEmail = employee?.officialEmail || employee?.personalEmail || employee?.emailAddress || COMPANY.email;

  return (
    <CardShell>
      <div className="flex h-full flex-col px-8 pb-7 pt-14">
        <CephasMark />

        <div className="mt-20 space-y-4">
          <ContactRow icon={Phone}>{COMPANY.phone}</ContactRow>
          <ContactRow icon={Mail}>
            <span className="block">{displayEmail};</span>
            <span className="block">admin@cephasict.com</span>
          </ContactRow>
          <ContactRow icon={Globe2}>{COMPANY.website}</ContactRow>
          <ContactRow icon={Linkedin}>{COMPANY.linkedin}</ContactRow>
          <ContactRow icon={MapPin}>{COMPANY.address}</ContactRow>
        </div>
      </div>
    </CardShell>
  );
}

function StaffIdCard({ employee, photo }) {
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [downloading, setDownloading] = useState("");
  const [error, setError] = useState("");

  const downloadCard = async (side) => {
    const node = side === "front" ? frontRef.current : backRef.current;
    if (!node) return;

    try {
      setError("");
      setDownloading(side);
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: "#ffffff",
        imagePlaceholder: createInitialsAvatar(getEmployeeName(employee)),
      });
      const link = document.createElement("a");
      link.download = getDownloadName(employee, side);
      link.href = dataUrl;
      link.click();
    } catch {
      setError("Unable to download the ID card image. Please try again.");
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-[#071C50]">Staff ID Card</p>
          <p className="text-sm text-gray-500">Preview and download the front or back of this staff ID card.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => downloadCard("front")}
            disabled={downloading === "front"}
            className="inline-flex items-center gap-2 rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <Download className="h-4 w-4" />
            {downloading === "front" ? "Downloading..." : "Front PNG"}
          </button>
          <button
            type="button"
            onClick={() => downloadCard("back")}
            disabled={downloading === "back"}
            className="inline-flex items-center gap-2 rounded-lg border border-[#1234CD] px-4 py-2 text-sm font-medium text-[#1234CD] disabled:opacity-60"
          >
            <Download className="h-4 w-4" />
            {downloading === "back" ? "Downloading..." : "Back PNG"}
          </button>
        </div>
      </div>

      {error ? <p className="text-sm text-red-600" role="alert">{error}</p> : null}

      <div className="flex items-center gap-2 text-sm font-medium text-[#071C50]">
        <Eye className="h-4 w-4" />
        ID card preview
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="overflow-x-auto rounded-lg border border-gray-100 bg-gray-50 p-4">
          <div ref={frontRef} className="inline-block">
            <FrontCard employee={employee} photo={photo} />
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-100 bg-gray-50 p-4">
          <div ref={backRef} className="inline-block">
            <BackCard employee={employee} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default StaffIdCard;
